import { debounce } from '~/helpers/debounce'

interface Response {
  error?: any,
  options: any,
  request: any,
  response: any
}

export const useWishlistStore = defineStore('wishlist', () => {
  const { $bus, $api } = useNuxtApp()
  const loading = ref<Boolean>(true)
  const items = ref<Array<Number>>([])
  const products = ref<Array<any>>([])
  const showMergeList = ref(false)
  const oldProducts = ref([])
  const wishlistId = ref('')
  const authStore = useAuthStore()

  const add = async (product: any) => {
    await $api('/wishlists/products', {
      method: 'POST',
      headers: {
        'Wishlist-Id': wishlistId.value
      },
      body: {
        cId: product.cId
      },
      onResponse: (response:Response) => {
        if (response.response.status === 201) {
          wishlistId.value = response.response._data.WishlistId
        }
        if (response.response.status === 207) {
          getOldProducts(response)
        }
      }
    })
    if (items.value.length > 47) {
      await sync()
      return
    }
    $bus.$emit('add-to-wishlist', {
      eventId: Date.now(),
      sourceUrl: document.location.href,
      product
    })
    items.value.push(product.cId)
  }

  const remove = async (product: any) => {
    await $api('/wishlists/products',
      {
        method: 'DELETE',
        headers: {
          'Wishlist-Id': wishlistId.value
        },
        body: {
          cId: product.cId
        },
        onResponse: (response:Response) => {
          if (response.response.status === 204) {
            items.value = []
          }
        }
      }
    )
    $bus.$emit('remove-from-wishlist', {
      eventId: Date.now(),
      sourceUrl: document.location.href,
      product
    })
    items.value = items.value.filter((row: any) => {
      return row !== product.cId
    })
    products.value = products.value.filter((item: any) => {
      return item.cId !== product.cId
    })
  }

  const getProducts = async () => {
    const data: any = await $api('/wishlists',
      {
        method: 'GET',
        headers: {
          'Wishlist-Id': wishlistId.value
        },
        params: {
          showDetails: true
        },
        onResponse: (response:Response) => {
          if (response.response.status === 207) {
            getOldProducts(response)
          }
        }
      })
    if (data) {
      products.value = data
    }

    return data
  }

  const retrieveAccountListId = async () => {
    await $api('/wishlists', {
      method: 'GET',
      onResponse: (response:Response) => {
        if (response.response.status === 201) {
          wishlistId.value = response.response._data.WishlistId
        }
      }
    })
  }

  const sync = async () => {
    if (!wishlistId.value || loading.value) {
      loading.value = false
      if (!wishlistId.value && authStore.loggedIn) {
        await retrieveAccountListId()
      }
      return
    }
    let resetList = true
    const data: any = await $api('/wishlists',
      {
        method: 'GET',
        headers: {
          'Wishlist-Id': wishlistId.value
        },
        onResponse: (response:Response) => {
          if (response.response.status === 207) {
            resetList = false
          }
          loading.value = false
        }
      })

    if (data) {
      if (!resetList) {
        loading.value = false
        return
      }
      items.value = []
      data?.cIds?.forEach((item: any) => {
        items.value.push(item)
      })

      loading.value = false
    }
  }

  const getOldProducts = (response: Response) => {
    oldProducts.value = response.response._data
    showMergeList.value = true
  }

  const mergeOldProducts = async (keepOldProducts: Boolean) => {
    await $api('/wishlists',
      {
        method: 'POST',
        headers: {
          'Wishlist-Id': wishlistId.value
        },
        body: {
          merge: keepOldProducts
        },
        onResponse: (response:Response) => {
          wishlistId.value = response.response._data.WishlistId
        }
      })
    showMergeList.value = false
    sync()
  }

  const clear = async () => {
    loading.value = true
    if (!products.value?.length) { return Promise.resolve() }
    for await (const item of products.value) {
      await remove(item)
    }

    items.value = []

    return await sync()
  }

  const amount = computed(() => {
    return items.value.length > 0
      ? items.value.length
      : false
  })

  const reset = () => {
    items.value = []
    products.value = []
    wishlistId.value = ''
  }

  return { mergeOldProducts, sync, retrieveAccountListId, remove, add, getProducts, clear, reset, loading, items, products, wishlistId, oldProducts, showMergeList, amount }
}, {
  persistedState: {
    persist: true,
    includePaths: ['wishlistId', 'items']
  }
})
